import React, { useCallback } from 'react';

import {
  Button,
  DropdownMenu,
  H2,
  HStack,
  Icon,
  Link,
  Separator,
  ShimmerText,
  Tag,
  Text,
  VStack,
} from '@betterment/samba-react';
import { cva } from '@betterment/samba-styled-system/css';
import { type RecipeVariantProps } from '@betterment/samba-styled-system/types';
import { noop, partition } from 'es-toolkit/compat';

import { urlBuilder } from '~/b4b/utils/urlBuilder';

import { CurrentEmployer, Employer, LoadingStatus } from './data';
import SidebarButton from './SidebarButton';

interface PlanSelectProps {
  currentEmployer?: CurrentEmployer | null;
  reload: () => void;
  loadingStatus: LoadingStatus;
  tasksLoadingStatus: LoadingStatus;
  employers?: Employer[];
  taskCounts: Record<string, number>;
  loadEmployersWithTasks: () => void;
}

type PlanSelectItemProps = {
  employer: Employer;
  isCurrent?: boolean;
  tasksLoadingStatus: LoadingStatus;
  taskCount?: number;
} & RecipeVariantProps<typeof planSelectItemStyles>;

const LoadingPlanSelectItem = () => {
  return (
    <HStack css={{ gap: 'space.800', py: 'space.150', justifyContent: 'space-between' }}>
      <ShimmerText textStyle="text25" css={{ height: '12px', my: 'space.50', width: '50%' }} />
      <ShimmerText textStyle="text25" css={{ height: '12px', my: 'space.50', width: 'size.300' }} />
    </HStack>
  );
};

const InactivePlanSelectItem = ({ employer }: { employer: Employer }) => {
  return (
    <DropdownMenu.Item
      onSelect={() => {}}
      css={{
        borderRadius: 'medium',
        alignItems: 'flex-start',
        gap: 'space.150',
        bg: 'background.neutral.active',
        p: 'space.100',
        minH: 0,
      }}
    >
      <VStack>
        <Text>{employer.name}</Text>
        <Text variant="text25">Inactive</Text>
      </VStack>
    </DropdownMenu.Item>
  );
};

const PlanBadge = ({ tasksLoadingStatus, taskCount }: PlanSelectItemProps) => {
  const isLoading = tasksLoadingStatus === 'loading';

  if (isLoading) {
    return <ShimmerText textStyle="text25" css={{ height: '12px', my: 'space.50', width: 'size.200' }} />;
  }

  if (taskCount !== undefined && taskCount > 0) {
    return <Tag accent="blue">{taskCount}</Tag>;
  }

  return null;
};

const planSelectItemStyles = cva({
  base: {
    textAlign: 'left',
    borderRadius: 'medium',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  variants: {
    isCurrent: {
      true: {
        bg: 'background.neutral.active',
      },
      false: {},
    },
  },
});

const PlanSelectItem = ({
  employer,
  tasksLoadingStatus,
  taskCount,
  isCurrent = false,
}: React.PropsWithChildren<PlanSelectItemProps>) => {
  return (
    <DropdownMenu.Item
      css={{ justifyContent: 'space-between' }}
      asChild={true}
      onSelect={noop}
      className={planSelectItemStyles({ isCurrent })}
    >
      <Link
        variant="ghostSecondary"
        href={urlBuilder.currentEmployer({ employerId: employer.id })}
        css={{ p: 'space.100', minH: 0, alignItems: 'center' }}
      >
        <Text variant="text50" css={{ maxW: '236px', flexGrow: 1, marginTop: 'space.25' }}>
          {employer.name}
        </Text>
        <PlanBadge employer={employer} tasksLoadingStatus={tasksLoadingStatus} taskCount={taskCount} />
      </Link>
    </DropdownMenu.Item>
  );
};

const isActive = (employer: Employer | null) => {
  return employer?.plan?.status === 'ACTIVE' || employer?.plan?.status === 'ONBOARDING';
};

const PlanSelect = ({
  currentEmployer,
  employers,
  taskCounts,
  loadingStatus,
  tasksLoadingStatus,
  reload,
  loadEmployersWithTasks,
}: PlanSelectProps) => {
  const [activeEmployers, inactiveEmployers] = partition(employers || [], isActive);

  // Handler to load employers with tasks when dropdown opens for the first time
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open && tasksLoadingStatus === 'idle') {
        loadEmployersWithTasks();
      }
    },
    [tasksLoadingStatus, loadEmployersWithTasks]
  );

  return (
    <DropdownMenu onOpenChange={handleOpenChange}>
      <DropdownMenu.Trigger asChild={true}>
        <SidebarButton label="Select plan">
          <SidebarButton.Content>
            <Text variant="text25" color="text.onPrimary">
              Plan
            </Text>
            <Text title={currentEmployer?.name} color="text.onPrimary" variant="text50">
              {currentEmployer?.name}
            </Text>
          </SidebarButton.Content>
        </SidebarButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        css={{
          zIndex: 5, // style-closet z-index for overlay elements
          px: 'space.200',
          pt: 'space.300',
          pb: 'space.200',
          minW: '320px',
          maxHeight: '100dvh',
          overflowY: 'auto',
        }}
        align="start"
        side="right"
      >
        <H2 variant="title100" css={{ pb: 'space.150', px: 'space.100' }}>
          Select plan
        </H2>
        {loadingStatus === 'error' && (
          <HStack css={{ gap: 'space.100', px: 'space.150', mb: 'space.200' }}>
            <Icon size="small" name="ExclamationCircle" />
            <Text variant="text50">
              Something went wrong
              <br />
              <Button variant="textPrimary" onClick={reload}>
                Try again
              </Button>
            </Text>
          </HStack>
        )}
        <VStack>
          {(loadingStatus === 'loading' || loadingStatus === 'error') && (
            <>
              <LoadingPlanSelectItem />
              <LoadingPlanSelectItem />
              <LoadingPlanSelectItem />
            </>
          )}
          {currentEmployer && !isActive(currentEmployer) && (
            <>
              <InactivePlanSelectItem employer={currentEmployer} />
              <Separator css={{ my: 'space.150' }} />
            </>
          )}

          {loadingStatus === 'loaded' &&
            activeEmployers.map(employer => (
              <PlanSelectItem
                key={employer.id}
                employer={employer}
                tasksLoadingStatus={tasksLoadingStatus}
                taskCount={taskCounts[employer.id]}
                isCurrent={currentEmployer?.id === employer.id}
              />
            ))}

          {inactiveEmployers.length > 0 && (
            <>
              <Separator css={{ my: 'space.150' }} />
              <Link
                variant="textPrimary"
                css={{ px: 'space.100', my: 'space.50', fontSize: 'fontSize.50' }}
                href="/plans/inactive/replace-with-real-href"
              >
                View inactive plans ({inactiveEmployers.length})
              </Link>
            </>
          )}
        </VStack>
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};

export default PlanSelect;
